import { getResult } from "../helpers/functional-util.js";
import session from "../session.js";

/**
 * `RepositoryContext` contains additional information regarding the context
 * of a request (it exposes data taken from the session). It is used when
 * instantiating models from repositories.
 *
 * @param {company} [spec.company] - Company used when materializing. Defaults
 * to the company in the session.
 * @param {period} [spec.period] - Period used when materializing. Defaults to
 * the period in the session.
 * @param {fiscalYear} [spec.fiscalYear] - Custom fiscalYear used when
 * materializing. There is usually no need to specify it as it will be taken
 * from the period's year, unless a custom or deleted fiscalYear has to be
 * used.
 * @param {{}} [spec.extraProperties] - Key/value pairs that will be added as
 * public properties to the receiver. Values can be functions that are evaluated
 * before their result is added as public property.
 */
export default class RepositoryContext {
	constructor({
		company = session.getCompany(),
		period = session.getPeriod(),
		fiscalYear,
		extraProperties = {},
	} = {}) {
		this._company = company;
		this._period = period;

		// custom fiscal year used instead of looking it up in the current
		// company. This is mostly useful for deleted fiscal years.
		//
		// Note that we do not want to set the fiscalYear in the context if
		// it is not a custom one, as periods should be looked up from the
		// company instead of the fiscal year. For instance, KPI values can
		// span over multiple years.
		this._fiscalYear = fiscalYear;

		this._extraProperties = {};
		this._initializeExtraProperties(extraProperties);
	}

	getCompany() {
		return this._company;
	}

	getPeriod() {
		return this._period;
	}

	getFiscalYear() {
		return this._fiscalYear;
	}

	setFiscalYear(newFiscalYear) {
		this._fiscalYear = newFiscalYear;
	}

	clone(spec) {
		return new this.constructor({
			company: this._company,
			period: this._period,
			fiscalYear: this._fiscalYear,
			extraProperties: this._extraProperties,
			...spec,
		});
	}

	getDimensionTree() {
		return this.getFiscalYear()?.getDimensionTree();
	}

	getFiscalYearById(fiscalYearId) {
		return this._fiscalYear?.getId() === fiscalYearId
			? this._fiscalYear
			: this.getCompany()?.findYearById(fiscalYearId);
	}

	/**
	 * Lookup the period by id using the company in the context.
	 *
	 * If a custom fiscalYear is set, use it instead. Note that when doing
	 * so, the periodId must be found within that fiscalYear, which is a
	 * restriction we do not have when looking up from the company.
	 */
	findPeriodById(periodId) {
		if (this._fiscalYear) {
			return this._fiscalYear.findPeriodById(periodId);
		}
		return this.findPeriodByIdWithinCompany(periodId);
	}

	findPeriodByIdWithinCompany(periodId) {
		return this.getCompany()?.findPeriodById(periodId);
	}

	findAccountByNumber(accountNumber) {
		return this._getAccounts()[accountNumber];
	}

	setExtraProperty(key, value) {
		this._extraProperties[key] = value;
		this[key] = getResult(value);
	}

	_initializeExtraProperties(extraProperties) {
		Object.keys(extraProperties).forEach((key) => {
			this.setExtraProperty(key, extraProperties[key]);
		});
	}

	/**
	 * Return an object mapping account numbers to accounts. Accounts
	 * are retrieved from the receiver's fiscal year (if present), the
	 * period and the company's fiscal years.
	 */
	_getAccounts() {
		this._accountsCache ||= this._buildAccountsCache();

		return this._accountsCache;
	}

	_buildAccountsCache() {
		const primaryPeriod = this._fiscalYear || this._period;
		const primaryAccounts = primaryPeriod.getAccounts();

		if (!this._company) return {};

		const allAccounts = this._company
			.getYears()
			.flatMap((year) => year.getAccounts());

		return [...allAccounts, ...primaryAccounts].reduce(
			(result, account) => {
				result[account.getNumber()] = account;

				return result;
			},
			{},
		);
	}
}
